import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/content/defaults";

const SiteContext = createContext(null);

export const useSite = () => useContext(SiteContext);

export function SiteProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState("");
  const projectsRequest = useRef(null);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [contentError, setContentError] = useState("");
  const { pathname } = useLocation();
  const contentRequest = useRef(null);

  const refreshContent = useCallback(() => {
    if (contentRequest.current) return contentRequest.current;
    contentRequest.current = (async () => {
      try {
        const { data } = await api.get("/content");
        setContent({ ...DEFAULT_CONTENT, ...data });
        setContentLoaded(true);
        setContentError("");
      } catch (e) {
        setContentError("Impossibile caricare i contenuti aggiornati. Riprova.");
      } finally {
        contentRequest.current = null;
      }
    })();
    return contentRequest.current;
  }, []);

  const refreshProjects = useCallback(() => {
    if (projectsRequest.current) return projectsRequest.current;
    setProjectsLoading(true);
    setProjectsError("");
    projectsRequest.current = (async () => {
      try {
        const { data } = await api.get("/projects", { timeout: 15000 });
        if (!Array.isArray(data)) throw new Error("Invalid projects response");
        setProjects(data);
        setProjectsLoaded(true);
      } catch {
        setProjectsError("Non è stato possibile caricare i cantieri. Riprova.");
      } finally {
        setProjectsLoading(false);
        projectsRequest.current = null;
      }
    })();
    return projectsRequest.current;
  }, []);

  useEffect(() => {
    refreshContent();
  }, [pathname, refreshContent]);

  useEffect(() => {
    refreshProjects();
  }, [pathname, refreshProjects]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshContent();
    };
    window.addEventListener("focus", refreshContent);
    window.addEventListener("pageshow", refreshContent);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", refreshContent);
      window.removeEventListener("pageshow", refreshContent);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshContent]);

  return (
    <SiteContext.Provider value={{ content, projects, projectsLoaded, projectsLoading, projectsError, contentLoaded, contentError, refreshContent, refreshProjects }}>
      {children}
    </SiteContext.Provider>
  );
}
